if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        // Ensure variant ID input is enabled and accessible
        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        } else {
          console.warn('Product form: Variant ID input not found');
        }
        
        // Remove any existing event listeners to prevent duplicates
        this.boundSubmitHandler = this.onSubmitHandler.bind(this);
        this.form.removeEventListener('submit', this.boundSubmitHandler);
        this.form.addEventListener('submit', this.boundSubmitHandler);
        
        // Also prevent button double-clicks
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        
        // Add click handler to button to prevent double-clicks
        if (this.submitButton) {
          this.boundButtonClickHandler = this.onButtonClick.bind(this);
          this.submitButton.removeEventListener('click', this.boundButtonClickHandler);
          this.submitButton.addEventListener('click', this.boundButtonClickHandler);
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.isSubmitting = false;
        this.currentRequestId = null; // Track current request to prevent duplicates
      }
      
      onButtonClick(evt) {
        // Prevent double-clicks on the button
        if (this.isSubmitting || this.submitButton.getAttribute('aria-disabled') === 'true') {
          evt.preventDefault();
          evt.stopPropagation();
          return false;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        evt.stopImmediatePropagation(); // Prevent other handlers from firing
        
        // Multiple layers of double-submission prevention
        if (this.isSubmitting) {
          evt.stopPropagation();
          return false;
        }
        
        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          evt.stopPropagation();
          return false;
        }
        
        // Check form-level data attribute
        if (this.form.dataset.submitting === 'true') {
          evt.stopPropagation();
          return false;
        }
        
        // Set all flags immediately to prevent any other submissions
        this.isSubmitting = true;
        this.form.dataset.submitting = 'true';
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.disabled = true; // Also use native disabled attribute
        
        // Get variant ID BEFORE disabling inputs (important!)
        // Disable form inputs to prevent any other interactions (but keep variant ID input accessible)
        const formInputs = this.form.querySelectorAll('input, button, select, textarea');
        formInputs.forEach(input => {
          if (input !== this.submitButton && input !== this.variantIdInput) {
            input.dataset.wasDisabled = input.disabled;
            input.disabled = true;
          }
        });
        
        // Ensure variant ID input is enabled so we can read its value
        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }

        this.handleErrorMessage();

        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        
        // Get variant ID from multiple possible sources
        let variantId = formData.get('id');
        
        // If not in formData, try to get from the input element directly
        if (!variantId && this.variantIdInput) {
          // Temporarily enable if disabled to read value
          const wasDisabled = this.variantIdInput.disabled;
          if (wasDisabled) {
            this.variantIdInput.disabled = false;
          }
          variantId = this.variantIdInput.value;
          if (wasDisabled) {
            this.variantIdInput.disabled = true;
          }
        }
        
        // Validate that variant ID exists and is valid
        if (!variantId || variantId === '' || variantId === 'null' || variantId === 'undefined') {
          console.error('Product form: Variant ID is missing or invalid', {
            formDataId: formData.get('id'),
            inputValue: this.variantIdInput?.value,
            inputDisabled: this.variantIdInput?.disabled,
            input: this.variantIdInput,
            allFormData: Object.fromEntries(formData.entries())
          });
          
          // Reset submission flags
          this.isSubmitting = false;
          this.form.dataset.submitting = 'false';
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.disabled = false;
          this.submitButton.classList.remove('loading');
          const spinner = this.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
          
          // Re-enable form inputs
          const formInputs = this.form.querySelectorAll('input, button, select, textarea');
          formInputs.forEach(input => {
            const wasDisabled = input.dataset.wasDisabled === 'true';
            input.disabled = wasDisabled;
            delete input.dataset.wasDisabled;
          });
          
          // Show error message
          const errorMessage = 'Please select a product variant';
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: null,
            errors: errorMessage,
            message: errorMessage,
          });
          this.handleErrorMessage(errorMessage);
          
          return;
        }
        
        // Ensure variant ID is set in formData (in case it wasn't captured)
        if (!formData.get('id') && variantId) {
          formData.set('id', variantId);
        }
        
        // Always add quantity 1 when adding to cart from product form
        // Quantity updates should only happen via cart-quantity-select in cart drawer
        formData.set('quantity', '1');
        
        // Verify the data before sending
        const finalVariantId = formData.get('id');
        const finalQuantity = formData.get('quantity');
        
        if (!finalVariantId || finalVariantId === '' || finalVariantId === 'null') {
          console.error('Product form: Variant ID is still missing after validation', {
            variantId,
            finalVariantId,
            formDataEntries: Array.from(formData.entries())
          });
          
          // Reset and show error
          this.isSubmitting = false;
          this.form.dataset.submitting = 'false';
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.disabled = false;
          this.submitButton.classList.remove('loading');
          const spinner = this.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
          
          // Re-enable form inputs
          const formInputs = this.form.querySelectorAll('input, button, select, textarea');
          formInputs.forEach(input => {
            const wasDisabled = input.dataset.wasDisabled === 'true';
            input.disabled = wasDisabled;
            delete input.dataset.wasDisabled;
          });
          
          const errorMessage = 'Please select a product variant';
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: null,
            errors: errorMessage,
            message: errorMessage,
          });
          this.handleErrorMessage(errorMessage);
          
          return;
        }
        
        // Generate unique request ID to track this specific submission
        const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const storedRequestId = requestId; // Store in closure for finally block
        this.currentRequestId = requestId;
        
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        
        // Log request data for debugging (only in development)
        if (window.cartFlowDebugger && window.cartFlowDebugger.enabled) {
          console.log('Product form: Sending cart add request', {
            variantId: finalVariantId,
            quantity: finalQuantity,
            hasSections: formData.has('sections')
          });
        }
        
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            // Check if this response is for the current request (prevent handling stale responses)
            if (this.currentRequestId !== storedRequestId) {
              console.warn('Ignoring response for outdated request');
              return;
            }
            
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            // Only reset if this is still the current request
            if (this.currentRequestId === storedRequestId) {
              // Reset all submission flags and UI states
              this.isSubmitting = false;
              this.form.dataset.submitting = 'false';
              this.currentRequestId = null;
            }
            
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            
            const spinner = this.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');
            
            // Re-enable form inputs
            const formInputs = this.form.querySelectorAll('input, button, select, textarea');
            formInputs.forEach(input => {
              const wasDisabled = input.dataset.wasDisabled === 'true';
              input.disabled = wasDisabled;
              delete input.dataset.wasDisabled;
            });
            
            // Re-enable submit button only if no error
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
              this.submitButton.disabled = false;
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
      
      disconnectedCallback() {
        // Clean up event listeners when element is removed
        if (this.form && this.boundSubmitHandler) {
          this.form.removeEventListener('submit', this.boundSubmitHandler);
        }
        if (this.submitButton && this.boundButtonClickHandler) {
          this.submitButton.removeEventListener('click', this.boundButtonClickHandler);
        }
      }
    }
  );
}
